import { useQueries, useQueryClient } from '@tanstack/react-query';
import { AdminVideo } from '../common/model/dto/video/admin-video';
import { VideoUploadItem } from '../context/video-upload.types';
import { getVideo } from '../services/admin/admin-video.service';
import {
  ADMIN_VIDEO_KEY,
  ADMIN_VIDEOS_KEY,
  applyVideoUpdate,
} from '../common/utils/video-query-cache';
import { isAwaitingChange, isSwap } from '../common/utils/video-status-utils';
import { VIDEO_STATUS_POLL_MS } from './use-video-status-polling';

export function useUploadJobVideos(
  uploads: VideoUploadItem[]
): Map<string, AdminVideo> {
  const queryClient = useQueryClient();
  const finished = uploads.filter(({ phase }) => phase === 'finished');

  const results = useQueries({
    queries: finished.map((item) => ({
      queryKey: [ADMIN_VIDEO_KEY, item.videoId],
      queryFn: async () => {
        const previous =
          queryClient.getQueryData<AdminVideo>([
            ADMIN_VIDEO_KEY,
            item.videoId,
          ]) ?? item.video;
        const latest = await getVideo(item.videoId);

        applyVideoUpdate(queryClient, latest);

        if (isSwap(previous, latest)) {
          await queryClient.invalidateQueries({ queryKey: [ADMIN_VIDEOS_KEY] });

          if (latest.replaces_video_id) {
            await queryClient.invalidateQueries({
              queryKey: [ADMIN_VIDEO_KEY, latest.replaces_video_id],
            });
          }
        }

        return latest;
      },
      initialData: item.video,
      staleTime: VIDEO_STATUS_POLL_MS,
      refetchInterval: (query: { state: { data?: AdminVideo } }) =>
        query.state.data && isAwaitingChange(query.state.data)
          ? VIDEO_STATUS_POLL_MS
          : false,
    })),
  });

  return new Map(
    finished.map((item, index) => [
      item.videoId,
      results[index].data ?? item.video,
    ])
  );
}
